<template>
  <div>
    <menu-group class="itemheader">
      <div>
        <h1 class="itemheader__title">
          {{ $t("objectNames.model") }}
        </h1>
      </div>
    </menu-group>

    <drawer
      v-model:open="openDrawers.modelInfo"
      :title="$t('labels.modelInfo')"
    >
      <div class="column">
        <input-text
          :label="$t('labels.title')"
          :value="modelTitle"
          :placeholder="loadedModel.name"
          @update:value="updateProject('title', $event)"
        />

        <input-texteditor
          :label="$t('labels.description')"
          :value="modelDescription"
          @update:value="updateProject('description', $event)"
        />
      </div>
    </drawer>

    <!-- QUESTION -->
    <drawer
      v-model:open="openDrawers.question"
      :title="$t('labels.systemContext')"
    >
      <div class="column gap-4">
        <!-- QUESTION TEXT -->
        <input-texteditor
          ref="questionTextField"
          :label="$t('labels.question')"
          :value="question.text"
          @update:value="updateQuestion('text', $event)"
        />

        <!-- MAIN QUESTION -->
        <input-switch
          :value="!question.hidden"
          :label="$t('labels.isQuestionVisible')"
          :label-right="true"
          @update:value="updateQuestion('hidden', !$event)"
        />

        <hr />

        <round-duration-chooser
          v-model:round-unit="settingsStore.simulationRoundUnit"
          v-model:round-length="settingsStore.simulationRoundLength"
          v-model:round-amount="settingsStore.simulationRoundAmount"
          v-model:duration-type="settingsStore.simulationDurationType"
          v-model:time-start="settingsStore.simulationTimeStart"
          v-model:time-end="settingsStore.simulationTimeEnd"
          v-model:round-customized="settingsStore.simulationRoundCustomized"
          v-model:round-name="settingsStore.simulationRoundName"
        />
      </div>
    </drawer>

    <drawer
      v-if="features.target || features.resource || features.index"
      v-model:open="openDrawers.elements"
      :title="$t('objectNames.element', 2)"
    >
      <div class="column">
        <!-- TARGET LIST -->
        <details-block v-if="features.target" v-model:open="openDrawers.targets">
          <template #header>
            <h4 class="label mt-0">{{ $t("objectNames.target", 2) }}</h4>
          </template>
          <element-list
            type="target"
            :highlight="true"
            class="pv-2"
            @select="selectMain"
          />
        </details-block>

        <details-block v-if="features.resource" v-model:open="openDrawers.resources">
          <template #header>
            <h4 class="label mt-0">{{ $t("objectNames.resource", 2) }}</h4>
          </template>
          <element-list
            type="resource"
            :highlight="true"
            class="pv-2"
            @select="selectMain"
          />
        </details-block>

        <details-block v-if="features.index" v-model:open="openDrawers.indices">
          <template #header>
            <h4 class="label mt-0">{{ $t("objectNames.index", 2) }}</h4>
          </template>
          <element-list
            type="index"
            :highlight="true"
            class="pv-2"
            @select="selectMain"
          />
        </details-block>
      </div>
    </drawer>

    <!-- STAKEHOLDER LIST -->
    <drawer
      v-if="features.stakeholder"
      v-model:open="openDrawers.stakeholders"
      :title="$t('objectNames.stakeholder', 2)"
    >
      <stakeholder-list />
    </drawer>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";

import Drawer from "@core/ui/DrawerBlock.vue";
import MenuGroup from "@core/ui/MenuGroup.vue";
import DetailsBlock from "@core/ui/DetailsBlock.vue";

import InputText from "@core/input/InputText.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import InputSwitch from "@core/input/InputSwitch.vue";

import ElementList from "@editor/items/element/ElementList.vue";
import StakeholderList from "@editor/items/stakeholder/StakeholderList.vue";
import RoundDurationChooser from "@editor/items/simulationRound/RoundDurationChooser.vue";

import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useQuestionStore } from "@editor/items/question/question.store";
import { useProjectStore } from "@editor/store/project.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useLoadedModel } from "@editor/store/loadedModel.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useFeatures } from "@editor/store/features.store";

import type { ProjectState } from "@editor/store/project.store";
import type { SelectableType } from "@editor/store/editor/selection.store";
import type { QuestionItem } from "../question/QuestionItem";

const { loadedModel } = useLoadedModel();

const settingsStore = useSettingsStore();
const selectionStore = useSelectionStore();
const openDrawers = useDrawerStates();

const selectedItem = computed(() => {
  return selectionStore.selectedItem;
});

function selectMain(item: { type: SelectableType; id: number }) {
  return selectionStore.select(item);
}

const features = useFeatures();

// ==================
// MODEL INFO MENU

const modelTitle = computed(() => {
  return useProjectStore().title;
});
const modelDescription = computed(() => {
  return useProjectStore().description;
});

function updateProject<K extends keyof ProjectState>(
  key: K,
  value: ProjectState[K]
) {
  useProjectStore().set({
    id: 0,
    [key]: value
  });
}

// ==================
// QUESTION MENU

const question = useQuestionStore();

const questionTextField = ref<InstanceType<typeof InputTexteditor>>();

function selectQuestionSettings() {
  openDrawers.question = true;
  nextTick(() => {
    questionTextField.value?.focus();
  });
}

function updateQuestion<K extends keyof QuestionItem>(
  key: K,
  value: QuestionItem[K]
) {
  question.set({
    id: 0,
    [key]: value
  });
}



// ==================
// UPDATES

watch(selectedItem, (item) => {
  if (item == null) {
    return;
  }
  if (item.type === "question") {
    selectQuestionSettings();
  }
});

onMounted(() => {
  if (selectedItem.value != null) {
    // scroll to area-creation
    if (selectedItem.value.type === "question") {
      selectQuestionSettings();
    }
  }
});
</script>

<style lang="scss">
.elpos {
  padding: 0.2em 0;
  display: flex;
  box-sizing: border-box;
  justify-content: space-between;
}

.elpos input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.5em;
  margin: 0 0.1em;
}
</style>
